import { KawaseBlurFilter } from '@pixi/filter-kawase-blur';
import { vec2 } from '@tlaukkan/tsm';
import * as PIXI from 'pixi.js';
import { defaultVertex, Filter, RenderTexture, settings, systems } from 'pixi.js';
import { Camera } from '../../../../shared/constants';
import { RenderEngine } from '../renderEngine';
import depthOfFieldFrag from '../shaders/fragment/depthOfFieldFrag';
import kawaseBlurFrag from '../shaders/fragment/kawaseBlur';
import basicVertex from "../shaders/vertex/default";
import { createQuadMesh } from '../spriteFactory';

/**The out of focus blur is rendered at this fraction of the screen resolution. It's blurry anyway, so lower resolution barely shows */
const BlurResolution = 0.5;

interface DepthOfFieldFilterOptions {
	kernels: number[] | null,
	blur: number,
	quality: number,
	pixelSize: number,
	resolution: number,
}

export class DepthOfField extends Filter {

	/**Only holds the blur settings (kernels, pixel size). The blur itself is done with blurMesh, so it can render at lower resolution */
	private _blurFilter: KawaseBlurFilter;
	private _resolution: number;
	private blurShader = new PIXI.Program(basicVertex, kawaseBlurFrag);
	private blurMesh: PIXI.Mesh | null = null;
	private blurTextures: PIXI.RenderTexture[] = [];

	focusDistance: number = Camera.StandardFocalDistance;
	dirtMap: PIXI.Texture | null = null;

	constructor(
		public engine: RenderEngine,
		public depth: PIXI.RenderTexture,
		public membraneDepth: PIXI.RenderTexture,
		options?: Partial<DepthOfFieldFilterOptions>) {

		super(defaultVertex, depthOfFieldFrag);

		//this.padding = 32;

		const opt: DepthOfFieldFilterOptions = Object.assign({
			kernels: null,
			blur: 2,
			quality: 2,
			pixelSize: 1,
			resolution: settings.FILTER_RESOLUTION,
		}, options);

		this._resolution = opt.resolution;

		const { kernels, blur, quality, pixelSize, resolution } = opt;

		this._blurFilter = kernels ?
			new KawaseBlurFilter(kernels) :
			new KawaseBlurFilter(blur, quality);
		this.pixelSize = pixelSize;
		this.resolution = resolution;
	}

	/**Blur textures with the same layout as the input (same logical size, so the same uvs line up) but fewer pixels */
	private prepareBlurTextures(input: RenderTexture) {
		const existing = this.blurTextures[0];
		const resolution = input.baseTexture.resolution * BlurResolution;
		if (existing && existing.width === input.width && existing.height === input.height && existing.baseTexture.resolution === resolution) return;

		this.blurTextures.forEach(texture => texture.destroy(true));
		this.blurTextures = [0, 1].map(() => PIXI.RenderTexture.create({ width: input.width, height: input.height, resolution }));
		this.blurMesh?.destroy();
		this.blurMesh = createQuadMesh(input.width, input.height, this.blurShader, { texture: null, uOffset: [0, 0] });
	}

	/**Kawase blur of the input, one pass per kernel, ping ponging between the two blur textures. Returns the blurred texture */
	private renderBlur(input: RenderTexture) {
		this.prepareBlurTextures(input);
		const mesh = this.blurMesh!;
		const pixelSize = this._blurFilter.pixelSize as any;
		const pixelX = typeof pixelSize === 'number' ? pixelSize : (pixelSize.x ?? pixelSize[0]);
		const pixelY = typeof pixelSize === 'number' ? pixelSize : (pixelSize.y ?? pixelSize[1]);

		let source: PIXI.Texture = input;
		this._blurFilter.kernels.forEach((kernel, i) => {
			const target = this.blurTextures[i % 2];
			//Offsets are in the input's uvs, so the blur is the same size on screen no matter the blur resolution
			const offset = kernel + 0.5;
			mesh.shader.uniforms.texture = source;
			mesh.shader.uniforms.uOffset = [offset * pixelX / input.width, offset * pixelY / input.height];
			this.engine.renderer.render(mesh, target);
			source = target;
		});

		//This nulling out is necessary to avoid a complaint from chrome about a feedback loop
		//"GL_INVALID_OPERATION: Feedback loop formed between Framebuffer and active Texture."
		mesh.shader.uniforms.texture = null;
		return source;
	}

	/**
	 * Override existing apply method in PIXI.Filter
	 * @private
	 */
	apply(filterManager: systems.FilterSystem, input: RenderTexture, output: RenderTexture, clear: boolean, currentState?: any) {
		//Only reads the input, so no need to copy it first
		const blurred = this.renderBlur(input);

		const mouse = this.engine.getMouseCoords()
		mouse.x = mouse.x / this.engine.renderer.width;
		mouse.y = mouse.y / this.engine.renderer.height;

		this.uniforms.depth = this.depth;
		this.uniforms.membrane = this.membraneDepth;
		this.uniforms.color = input;
		this.uniforms.blur = blurred;
		this.uniforms.dirt = this.dirtMap;
		this.uniforms.mouse = mouse.xy;

		const maxFocusDistance = 2; //allow it to go up enough as to put everything totally out of focus
		this.focusDistance = Math.max(0, Math.min(maxFocusDistance, this.focusDistance));

		this.uniforms.focalDistance = this.focusDistance;
		this.uniforms.focalFalloff = Camera.StandardFocalFalloff;
		this.uniforms.texelSize = [1 / input.width, 1 / input.height];

		filterManager.applyFilter(this, input, output, clear);
	}

	/**
	 * The resolution of the filter.
	 * 
	 * @member {number}
	 */
	//@ts-ignore
	get resolution() {
		return this._resolution;
	}
	set resolution(value) {
		this._resolution = value;

		if (this._blurFilter) {
			this._blurFilter.resolution = value;
		}
	}

	/**
	 * Sets the kernels of the Blur Filter
	 *
	 * @member {number}
	 * @default 4
	 */
	get kernels() {
		return this._blurFilter.kernels;
	}
	set kernels(value) {
		this._blurFilter.kernels = value;
	}

	/**
	 * Sets the strength of the Blur properties simultaneously
	 *
	 * @member {number}
	 * @default 2
	 */
	get blur() {
		return this._blurFilter.blur;
	}
	set blur(value) {
		this._blurFilter.blur = value;
	}

	/**
	 * Sets the quality of the Blur Filter
	 *
	 * @member {number}
	 * @default 4
	 */
	get quality() {
		return this._blurFilter.quality;
	}
	set quality(value) {
		this._blurFilter.quality = value;
	}

	/**
	 * Sets the pixelSize of the Kawase Blur filter
	 *
	 * @member {number|number[]|Point}
	 * @default 1
	 */
	get pixelSize() {
		return this._blurFilter.pixelSize;
	}
	set pixelSize(value) {
		this._blurFilter.pixelSize = value;
	}
}
